/**
 * What a STEP is: the shape of its completion, and the task-tree lookups two of them need.
 *
 * `job.ts` is to a job what this file is to a step — the shapes, and nothing that acts. It was called
 * `step-closure.ts` while a step's rules were a closure a job type wrapped by hand; they are a declaration now
 * (`onStepComplete`), so the name went with the concept.
 *
 * A job type declares `onStepComplete` and the framework does the rest: when a row of that type reaches a terminal
 * status, `job-hooks.ts` asks whether the exam has any live row left of that type's STEP, and if not runs everything
 * declared here exactly once. So a job type says WHAT happens when its step finishes and never how or when.
 *
 * THE STEP IS DERIVED, NOT DECLARED — `stepTypesOf(type)` in `job.ts` reverses `STEP_JOB_TYPES`, and a type
 * belonging to no setup step is its own step. That is the last thing that used to be stated twice: the closure this
 * replaced carried `step: 'gradingScheme'` next to a `setupStepSync(...)` call, so three producers per step meant six
 * places where a name and a wiring had to agree. Nothing states a step name any more.
 *
 * WHERE A DECLARATION LIVES follows from how many job types produce the step:
 *
 *  - ONE producer → in the job type. `answer` is declared in `answer-extraction.job.ts`, `grading` in
 *    `grading.job.ts`; `task` declares nothing, because `runStructureStep` writes its own outcome and distinguishes
 *    cases the rows cannot (descriptions failed but the structure is usable).
 *  - SEVERAL producers → `steps/*.step.ts`, imported by each. `masterSolution` and `gradingScheme` have three types
 *    each, so no single one can own their rules — and `validateStepCompletionWiring` checks at boot that all three
 *    point at the SAME object, which is the failure this shape could otherwise still have.
 *
 * This file holds no policy of its own: no status writes, no dispatches, nothing about which step is which. That is
 * what keeps it importable from both directions without a runtime cycle — the step files and `job.ts` import these
 * types, `step-completion.ts` runs them, and neither imports the other.
 */

import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../exam.js';
import { progressRecordOf, StepState, type SolutionProducer } from '../../extraction-progress.js';
import type { SetupStepDelta } from '../pending-tasks/extraction-or-generation-finished.js';
import type { BatchIntent } from './batch-store.js';
import type { DispatchScope, JobDoc, SetupStep } from './job.js';

/**
 * WHAT THE WAVE THAT JUST ENDED CONSISTED OF — the rows, what each was asked for, and what each did.
 *
 * This is what `exam_jobs` being the record of what HAPPENED buys, and it is the thing exam state could not express:
 * a snapshot holds one current value per field, while a run is a set of past events. An announcement needs the set.
 *
 * ASKED FOR vs DID, and both are here because they are genuinely different facts. A row asked to grade eight
 * submissions that graded six is not described by either number alone; a row cancelled before it ran was asked for
 * something and did nothing at all. So `intents` and `payload` are the asks, `result` is the outcome, and `succeeded`
 * / `failed` say which asks actually landed.
 *
 * WHY `intents` IS A FIELD OF ITS OWN AND NOT JUST THE ROWS' PAYLOADS. A trigger whose every unit deduped onto work
 * already queued produces NO ROW — the normal, correct outcome of a dedupe — so its cause would have nowhere to live.
 * The enqueue pushes it onto the row that absorbed the collision instead (`JobDoc.intents`), which is why a payload
 * has to stay immutable and this does not. It is what the exam-level re-grade ledger existed to paper over, without
 * an accumulator that has to be drained.
 */
export interface BatchOutcome {
  batchKey: string;
  /** Every trigger's ask, including those whose rows all deduped away. */
  intents: BatchIntent[];
  /** Rows that ran and did their work — `done` and not skipped. */
  succeeded: JobDoc[];
  /** Rows that ended `failed` — a genuine attempt that ran out of retries. NOT `cancelled`: that status means the
   *  reason to run disappeared (nothing was ever uploaded, the target is already covered, ...), which is a no-op,
   *  not a failure — see `cancelJob`. */
  failed: JobDoc[];
}

/** The step that just closed, as its own declarations see it. No `step` field — a declaration belongs to exactly one. */
export interface FinishedRun {
  examId: string;
  examState: any;
  /** Task `_id`s whose content this run actually changed. Empty for a step with no artifact. */
  changedTasks: string[];
  /** The wave itself — see `BatchOutcome`. What an announcement should be built from. */
  batch: BatchOutcome;
}

/**
 * What a job type declares happens when a dispatch OPENS its step — the mirror of `StepCompletion`.
 *
 * The step is derived, exactly as it is at the other end, so nothing here names one. What every opener had in common
 * was `beginProgressRun(<my step>, { reupload })`: eight declarations wrote that line by hand, which is eight chances
 * to open the run after the rows instead of before it — the ordering that decides whether a step announces itself
 * against this pass's total or the previous one's.
 */
export interface StepStart {
  /**
   * Whether this run is a REPEAT of a step that already ran to a conclusion. Describes the TRIGGER, not the state,
   * which is why it is declared rather than derived later, when the run closes and nothing remembers how it started.
   *
   * Omitted lets `beginProgressRun` decide, which is "this step already reached COMPLETED or ERROR once" — right for a
   * delivery. A refine declares `false`: it is a correction to an artifact, not a fresh delivery of it, and the
   * announcement words the two differently.
   *
   * A FUNCTION when the trigger is a property of the DISPATCH rather than of the type, which is `answerExtraction`:
   * the same type serves a new round of submissions and a retry of failed ones, and only the scope knows which. A
   * constant here made a retry announce itself as a re-upload, because the derivation is right for the round and
   * wrong for the retry. Returning `undefined` falls back to the derivation, so a scope only has to speak up for the
   * cases it actually knows better than the record does.
   */
  reupload?: boolean | ((scope: DispatchScope) => boolean | undefined);
  /**
   * OTHER steps' progress records to bring into existence, so the UI can show the whole pipeline from the start.
   *
   * SEEDED, NEVER RESET — `seedProgressRecord` only writes a record that does not exist. The structure extraction is
   * the one type that wants this, and it used to do it in its handler with `createStep` / `initSolutionProgress`, both
   * of which reset unconditionally. That wiped an answer run already opened by an upload whose rows were waiting for
   * the structure to be confirmed: the bar fell back to pending and the step could then never close, because a
   * completion refuses a record that is neither IN_PROGRESS nor COMPLETED.
   */
  seed?: readonly SetupStep[];
  /**
   * Anything else this step needs written before its rows exist — the status, the unit total, the producer.
   *
   * Only the two types that know their total at dispatch time declare it: `answerExtraction` counts the submissions,
   * and the generations count the solution roots. An extraction leaves it to its handler, which is the first thing
   * that knows whether there was anything to read.
   */
  open?: (examId: string, scope: DispatchScope) => Promise<unknown>;
}

/**
 * What a job type declares happens when the last live row of its step goes terminal.
 *
 * Every field is optional, and which ones a step declares says what kind of step it is: the four SETUP steps keep a
 * progress record on the exam and announce, so they declare `progress`; `grading` keeps neither and declares only
 * `finish`. A type that declares nothing gets no hook at all.
 */
export interface StepCompletion {
  /**
   * The exam-state progress record this step closes, for the steps that keep one.
   *
   * Omitting it is the statement that this step has no record to close — not an oversight. `grading` is the case:
   * its own session refcount and the re-grade ledger are what it closes, neither of which is a `SetupStep`.
   */
  progress?: {
    /** The i18n key the record closes with. */
    message: string;
    /**
     * The i18n key for a wave that produced NOTHING and had failures — the step closes ERROR instead of COMPLETED.
     *
     * Optional: without it such a wave still closes as ERROR, keeping `message`. Declare it where the step has
     * wording for the failure ("progress.errorExtractingAnswers"), because "answers extracted" on an ERROR record is
     * a worse lie than a generic one.
     */
    errorMessage?: string;
    /**
     * False holds the step open. Reads the exam's `examState` and this step's own record.
     *
     * GENERATION needs one, extraction does not, and the split is not cosmetic. With one job per root, a drained
     * queue means "every root job we created is resolved", not "no more roots will appear" — roots arrive as the
     * structure extraction creates them, and a grading scheme's roots only become workable once their master
     * solutions land. An EXTRACTION reads what the teacher uploaded in a single pass and is explicitly not gated
     * on the master solution (see `gs-extraction.job.ts`), so applying the generation's precondition to it would
     * hold a perfectly finished grading-scheme upload open behind a master solution that errored.
     */
    precondition?: (examState: any, record: Record<string, any>) => boolean;
    /** What this step contributes to the announcement, from its own record. Null means "not announced". */
    delta: (record: Record<string, any>) => SetupStepDelta | null;
  };
  /**
   * Work that is neither a progress write nor a release, run once per drained wave and before `followUp`.
   *
   * `grading` closes its session and announces the re-grade ledger here — the two things that make "this exam has
   * no grading row left" mean something, and neither of which is expressible as a progress record.
   */
  finish?: (run: FinishedRun) => Promise<void>;
  /**
   * What to release next. Returning TRUE suppresses the announcement, because whatever was released announces for
   * itself — announcing twice for one upload is the thing this return value exists to prevent.
   *
   * Detached by contract: a completion hook decides what happens next, it does not wait on minutes of model work.
   */
  followUp?: (run: FinishedRun) => Promise<boolean>;
}

/** Has another step already closed? The shape both solution preconditions are built from. */
export const stepIsCompleted = (examState: any, step: SetupStep): boolean =>
  progressRecordOf(examState, step)?.status === StepState.COMPLETED;

/**
 * Which producer wrote this record — the one field a solution step's announcement and precondition both read.
 *
 * THREE VALUES, NOT TWO. This used to fold anything that was not `generation` into `extraction`, which meant a refine
 * announced itself as an upload: the teacher heard that the scheme had been extracted, or fully generated, for a
 * correction to one task. An unset producer is still read as `extraction`, because that is what an artifact with no
 * recorded producer came from.
 */
export const producerOf = (record: Record<string, any>): SolutionProducer => {
  const producer = record.producer as SolutionProducer | undefined;
  return producer === 'generation' || producer === 'refine' ? producer : 'extraction';
};

/** The exam's task tree, for the lookups below. */
async function examTasksOf(examId: string): Promise<any[]> {
  const examCollection = await getCollection(Exam as any);
  const exam = await examCollection.findOne({ _id: examId } as any, { projection: { tasks: 1 } });
  return Array.isArray((exam as any)?.tasks) ? (exam as any).tasks : [];
}

/**
 * The main task each changed id sits under — its `_id` (the unit key `createdForTasks` is written in, so what a
 * pull addresses), its `taskNumber` (what the agents address tasks by), and the NODE itself, so a caller can ask
 * a question about the subtree without re-walking the tree to find it again.
 */
export interface MainTask {
  taskId: string;
  taskNumber: string;
  node: any;
}

export async function mainTasksForIds(examId: string, taskIds: string[]): Promise<MainTask[]> {
  const wanted = new Set(taskIds);
  const out = new Map<string, MainTask>();
  const walk = (nodes: unknown, main: MainTask | null): void => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes as Array<Record<string, unknown>>) {
      const id = typeof node._id === 'string' ? node._id : '';
      const number = typeof node.taskNumber === 'string' ? node.taskNumber : '';
      const root = main ?? (id && number ? { taskId: id, taskNumber: number, node } : null);
      if (id && root && wanted.has(id)) out.set(root.taskId, root);
      walk(node.subTasks, root);
    }
  };
  walk(await examTasksOf(examId), null);
  return [...out.values()];
}

/**
 * Task `_id`s → the `taskNumber`s everything downstream addresses tasks by, mapped to the MAIN task each one sits
 * under. A changed subtask means its main task's scheme and grades are what get redone.
 */
export async function mainTaskNumbersForIds(examId: string, taskIds: string[]): Promise<string[]> {
  return (await mainTasksForIds(examId, taskIds)).map(task => task.taskNumber);
}
